import type { TFunction } from 'i18next';
import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  SidebarWrapper,
  SidebarWrapperOverflowContainer,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { useResponsiveSidebarClose } from '@/components/organisms/Sidebar/ResponsiveSidebarPanel';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import type {
  ContactAsOf,
  ContactAsOfState,
  ContactChangeSource,
  ContactFieldChange,
} from '@/shared/types/contacts';
import { formatDate } from '@/util/dateUtils';

import { FieldGroup } from './DebtsTab';
import { useContactAsOf } from '../hooks/useContactAsOf';
import {
  contactFieldLabel,
  CUSTOM_FIELD_PREFIX,
  formatContactFieldValue,
} from '../utils/contactFields';

const DATE_TIME = 'yyyy-MM-dd HH:mm';

const TRACKED_FIELDS = [
  'name',
  'email',
  'phone',
  'external_id',
  'time_zone',
  'country_code',
  'residence_state',
  'contact_preferences',
  'deceased',
] as const;

const SOURCE_KEY: Record<ContactChangeSource, string> = {
  api: 'contacts.as_of.source.api',
  resolution: 'contacts.as_of.source.resolution',
  self_reported: 'contacts.as_of.source.self_reported',
  subscriber_upload: 'contacts.as_of.source.subscriber_upload',
  nba: 'contacts.as_of.source.nba',
  channels_preferences: 'contacts.as_of.source.channels_preferences',
  channels_rekey: 'contacts.as_of.source.channels_rekey',
};

const stateFields = (contact: ContactAsOfState, t: TFunction) => [
  ...TRACKED_FIELDS.map((field) => ({
    key: field,
    label: contactFieldLabel(field, t),
    value: formatContactFieldValue(contact[field], t),
  })),
  ...Object.entries(contact.custom_fields ?? {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => ({
      key: `${CUSTOM_FIELD_PREFIX}${key}`,
      label: key,
      value: formatContactFieldValue(value, t),
    })),
];

// A vouched write is a channel sync, such as a WhatsApp profile name, and the feed leaves it out.
const changeSource = (change: ContactFieldChange, t: TFunction): string | undefined => {
  if (change.claim_status === 'vouched') {
    return t('contacts.as_of.source.vouched');
  }
  return change.source ? t(SOURCE_KEY[change.source]) : undefined;
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h3 className="text-sm leading-5 font-medium text-muted-foreground">{children}</h3>
);

const ChangeRow = ({ change }: { change: ContactFieldChange }) => {
  const { t } = useTranslation();
  const details = [formatDate(change.changed_at, DATE_TIME), changeSource(change, t)].filter(
    Boolean
  );
  return (
    <li className="flex flex-col gap-0.5">
      <span className="text-sm leading-5 break-words text-foreground">
        <span className="font-medium">{contactFieldLabel(change.field, t)}</span>
        {': '}
        {formatContactFieldValue(change.value_at, t)} &rarr;{' '}
        {formatContactFieldValue(change.value_now, t)}
      </span>
      <span className="text-xs leading-4 text-muted-foreground">{details.join(' · ')}</span>
    </li>
  );
};

const historyNote = (asOf: ContactAsOf, t: TFunction): string | undefined => {
  if (!asOf.history_starts_at) {
    return t('contacts.as_of.no_history');
  }
  if (Date.parse(asOf.history_starts_at) > Date.parse(asOf.at)) {
    return t('contacts.as_of.history_starts', { 0: formatDate(asOf.history_starts_at, DATE_TIME) });
  }
  return undefined;
};

const AsOfBody = ({ asOf }: { asOf: ContactAsOf }) => {
  const { t } = useTranslation();

  if (!asOf.existed || !asOf.contact) {
    return (
      <span className="text-sm leading-5 text-muted-foreground">
        {t('contacts.as_of.not_existed')}
      </span>
    );
  }

  const note = historyNote(asOf, t);
  return (
    <>
      {note && <span className="text-sm leading-5 text-muted-foreground">{note}</span>}
      <FieldGroup className="px-0" fields={stateFields(asOf.contact, t)} />
      <Separator />
      <SectionTitle>{t('contacts.as_of.changed_since')}</SectionTitle>
      {asOf.changes.length === 0 ? (
        <span className="text-sm leading-5 text-muted-foreground">
          {t('contacts.as_of.no_changes')}
        </span>
      ) : (
        <ul className="flex flex-col gap-3">
          {asOf.changes.map((change) => (
            <ChangeRow key={change.field} change={change} />
          ))}
        </ul>
      )}
    </>
  );
};

interface ContactAsOfPanelProps {
  contactId: string;
  at: string;
}

export const ContactAsOfPanel = ({ contactId, at }: ContactAsOfPanelProps) => {
  const { t } = useTranslation();
  const { asOf, status } = useContactAsOf(contactId, at);
  const onClose = useResponsiveSidebarClose();
  useEscapeKey(onClose);

  const renderBody = () => {
    if (status === 'pending') {
      return <Spinner size="sm" />;
    }
    if (status === 'error' || !asOf) {
      return (
        <span className="text-sm leading-5 text-destructive">{t('contacts.as_of.failed')}</span>
      );
    }
    return <AsOfBody asOf={asOf} />;
  };

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle className="pr-4">
        <h2 className="truncate text-base leading-none font-medium text-foreground">
          {t('contacts.as_of.title', { 0: formatDate(at, DATE_TIME) })}
        </h2>
        <Button
          variant="ghost-neutral"
          size="icon-sm"
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
        >
          <XIcon size={16} className="text-muted-foreground" />
        </Button>
      </SidebarWrapperTitle>
      <SidebarWrapperOverflowContainer className="gap-4 pb-6">
        {renderBody()}
      </SidebarWrapperOverflowContainer>
    </SidebarWrapper>
  );
};
